import React, { useCallback, useState } from 'react';
import type { ColDef, EditableCallbackParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  LopHistoryDateFilter,
  isoToday,
  isoTwoWeeksAgo,
} from '../../components/LopHistoryDateFilter';
import {
  getIdxRatesHistory,
  saveIdxRatesHistoryBatch,
} from '../../api/idxRatesHistory';
import type { IdxRateHistory } from '../../types/IdxRateHistory';
import { t } from '../../i18n/t';

// "שערי מדדים היסטוריים" — composite-PK (idxId, dDate). Mirrors
// RatesHistoryPage. Same draft/active date-state pattern.
const columnDefs: ColDef<IdxRateHistory>[] = [
  {
    field: 'dDate',
    headerName: t('idxRatesHistory.columns.dDate'),
    width: 140,
    editable: (params: EditableCallbackParams<IdxRateHistory>) =>
      (params.data as { __status?: string } | undefined)?.__status === 'added',
    sort: 'desc',
  },
  {
    field: 'idxId',
    headerName: t('idxRatesHistory.columns.idxId'),
    width: 130,
    editable: (params: EditableCallbackParams<IdxRateHistory>) =>
      (params.data as { __status?: string } | undefined)?.__status === 'added',
    cellDataType: 'number',
  },
  {
    field: 'baseRate',
    headerName: t('idxRatesHistory.columns.baseRate'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'openRate',
    headerName: t('idxRatesHistory.columns.openRate'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'currRate',
    headerName: t('idxRatesHistory.columns.currRate'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
];

const newIdxRateHistoryDefaults = (): Partial<IdxRateHistory> => ({
  idxId: 0,
  dDate: isoToday(),
  baseRate: null,
  openRate: null,
  currRate: null,
});

export function IdxRatesHistoryPage() {
  const [draftFrom, setDraftFrom] = useState<string>(isoTwoWeeksAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);

  const getRows = useCallback(
    () => getIdxRatesHistory(activeFrom, activeTo),
    [activeFrom, activeTo],
  );
  const saveBatch = useCallback(
    (
      added: IdxRateHistory[],
      modified: IdxRateHistory[],
      deletedIdKeys: unknown[],
    ) =>
      saveIdxRatesHistoryBatch(
        added,
        modified,
        deletedIdKeys as string[],
        activeFrom,
        activeTo,
      ),
    [activeFrom, activeTo],
  );

  function handleApply() {
    setActiveFrom(draftFrom);
    setActiveTo(draftTo);
    setFetched(true);
  }

  return (
    <div className="lop-history-page">
      <LopHistoryDateFilter
        fromDate={draftFrom}
        toDate={draftTo}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={handleApply}
      />
      <LopCrudPage<IdxRateHistory>
        awaitingFetch={!fetched}
        resourceCode="idxRatesHistory"
        titleKey="idxRatesHistory.pageTitle"
        idField="idKey"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newIdxRateHistoryDefaults}
      />
    </div>
  );
}
